import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addTermsAndCondition,
  fetchTermsAndConditionById,
  updateTermsAndCondition,
} from "slices/Masters/terms-and-condition/thunk";
import { autocompleteDropdownStyles } from "Constants/Style";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const typeOptions = [
  "After Sales Service",
  "Delivery",
  "Guarantee",
  "Price",
  "Payment",
  "Scope of Supply",
  "Validity",
  "Other",
];

// Mapping from UI display values to backend enum values
const typeToEnumMap: Record<string, string> = {
  "After Sales Service": "After Sales Service",
  Delivery: "Delivery",
  Guarantee: "Guarantee",
  Price: "Price",
  Payment: "Payment",
  "Scope of Supply": "Scope of Supply",
  Validity: "Validity",
  Other: "Other",
};

// Reverse mapping from backend enum values to UI display values
const enumToTypeMap: Record<string, string> = {
  AFTER_SALES_SERVICE: "After Sales Service",
  DELIVERY: "Delivery",
  GUARANTEE: "Guarantee",
  PRICE: "Price",
  PAYMENT: "Payment",
  SCOPE_OF_SUPPLY: "Scope of Supply",
  VALIDITY: "Validity",
  OTHER: "Other",
};

const TermsAndConditionForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    type: "",
    options: "",
  });
  console.log("id in terms and condition form", id);
  // Fetch data on edit
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchTermsAndConditionById(id))
        .unwrap()
        .then((data: any) => {
          const termData = data.data || data;
          // Convert backend enum value to UI display value
          const backendType = termData.type || "";
          const displayType = enumToTypeMap[backendType] || backendType;
          setInitialData({
            type: displayType,
            options:
              termData.options || termData.term_text || termData.options || "",
          });
        })
        .catch(() => toast.error("Failed to fetch Terms & Condition details"))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: initialData.type || "",
      options: initialData.options || "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("Please select a type"),
      options: Yup.string().trim().required("Please enter options"),
    }),
    onSubmit: async (values) => {
      // Convert UI display value to backend enum value
      const backendType = typeToEnumMap[values.type] || values.type;

      const payload = {
        type: backendType,
        options: values.options.trim(),
      };

      setIsSubmitting(true);
      try {
        if (isEdit && id) {
          await dispatch(
            updateTermsAndCondition({
              terms_and_condition_id: id,
              payload: payload,
            }),
          ).unwrap();
          toast.success("Terms & Condition updated successfully");
        } else {
          await dispatch(addTermsAndCondition(payload)).unwrap();
          clearTableState(TableKeys.MASTER.TERMS_AND_CONDITIONS);
          toast.success("Terms & Condition added successfully");
        }
        navigate("/master/terms-and-condition");
      } catch (error: any) {
        toast.error(
          error?.message || "Something went wrong. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  const handleCancelButton = () => {
    navigate("/master/terms-and-condition");
  };

  if (isEdit && isLoading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Terms & Condition" : "Add Terms & Condition"}
        titleLink="/master/terms-and-condition"
        breadcrumbs={[
          { label: "Terms & Conditions", to: "/master/terms-and-condition" },
          {
            label: id ? "Edit Terms & Condition" : "Add Terms & Condition",
            to: "",
          },
        ]}
        backNavigationPath={-1}
      />
      <hr />

      <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <Row className="mb-3">
          <Col xs={12} md={6} className="mb-3">
            <Form.Label className="fw-semibold">
              Type <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={typeOptions}
              getOptionLabel={(option: any) =>
                typeof option === "string" ? option : option
              }
              value={formik.values.type || null}
              onChange={(event, selected) => {
                formik.setFieldValue("type", selected || "");
              }}
              onBlur={() => formik.setFieldTouched("type", true)}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) => opt,
                )
              }
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Type"
                  name="type"
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Col>

          <Col xs={12} className="mb-3">
            <Form.Label className="fw-semibold">
              Options <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              name="options"
              placeholder="Enter options"
              value={formik.values.options}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.options && Boolean(formik.errors.options)
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.touched.options && formik.errors.options}
            </Form.Control.Feedback>
          </Col>
        </Row>

        <Row>
          <Col
            xs={12}
            className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3"
          >
            <CancelButton
              onClick={handleCancelButton}
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              label="Submit"
            />
          </Col>
        </Row>
      </Form>
    </PageContainer>
  );
};

export default TermsAndConditionForm;
